/***
 *  Cost of quality widget - shows line graph and today, yesterday and last week values. User can switch between quantity and cost.
 */

/* eslint-disable react/prop-types */

import 'chartjs-adapter-dayjs-3';
import classNames from 'classnames';
import Flex from 'components/common/Flex';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween'; // load on demand
import isToday from 'dayjs/plugin/isToday';
import { getColor } from 'helpers/utils';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Card, Col, Container, Form, Row } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import Switch from 'react-switch';
import { getAllDownDays } from 'services/downDaysService';
import { getTurnover } from 'services/turnoverService';
import chartTrendline from 'chartjs-plugin-trendline';
import { Chart as ChartJS } from 'chart.js';
import { WidgetLoadKV, WidgetLoadKVBool, WidgetSaveKV } from 'helpers/widgetHelper';
ChartJS.register(chartTrendline);

/* COQWidget this widget is used to display a line chart of the cost of quality (COQ) data.
* It also shows the cost of quality data for today, yesterday, and last week. And allows the user to switch between cost and quantity.
* It uses the chart.js library to render the chart. */

// use dayjs plugins
dayjs.extend(isBetween);
dayjs.extend(isToday);

// ChartOptions is a functional component that takes in props and returns a line chart with trendline data.
// It uses the chart.js library to render the chart. The chart is responsive and will resize based on the parent container.
// The chart shows the cost of quality data for the given date range. The trendline is calculated using linear regression.
function ChartOptions(props) {
  let trendLineData = [];


  let tlSumX = 0;
  let tlSumY = 0;
  let tlSumXY = 0;
  let tlSumX2 = 0;
  let tlLen = 0;

  let chartItems = [];
  for (let i = 0; i < props.endDate.diff(props.startDate, 'day'); i++) {
    let current = props.startDate.add(i, 'day');
    let currentStr = current.format('YYYY-MM-DD');

    let chartItem = { x: currentStr, y: 0 };

    if (props.itemMap[currentStr]) {
      // COST or QTY?
      
      // We are showing actual value ( COST / QTY)
      if (props.isShowingValue)
      {
        if (props.isShowingCost) {
          chartItem.y = props.itemMap[currentStr].totalCost;
        } else {
          // QT
          chartItem.y = props.itemMap[currentStr].items.length;
        }
      } 
      // We are showing a percentage instead of actual value
      else {
        if (props.isShowingCost) {
          // let totalCostForCurrentDate = props.itemMap[currentStr].totalCost;
          let totalTurnoverForCurrentDate = props.itemMap[currentStr].totalCompanyTurnover;
          if (totalTurnoverForCurrentDate > 0)
          {
            chartItem.y = Number((props.itemMap[currentStr].totalCost * 100) / totalTurnoverForCurrentDate).toFixed(3);;
            if (chartItem.y > 100.0) chartItem.y = 100.0;
          }
          
        } else {
          // QT
          chartItem.y = Number((props.itemMap[currentStr].items.length * 100) / props.itemMap[currentStr].totalCompanyDeliveredJobCount).toFixed(3);
          if (chartItem.y > 100.0) chartItem.y = 100.0;
        }
      }
      
    }

    // Trendline calculation

    // Add chart item to data
    let is_down_day =
      props.downDays[currentStr] &&
      props.downDays[currentStr].shutdown === true;
    let is_weekend = current.day() % 6 === 0;
    if (chartItem.y > 0 || (!is_down_day && !is_weekend)) {
      let timestamp = current.unix();
      tlSumX += timestamp;
      tlSumY += chartItem.y;
      tlSumXY += timestamp * chartItem.y;
      tlSumX2 += timestamp * timestamp;
      tlLen++;

      chartItems.push(chartItem);
    }
  }

  const slope =
    (tlLen * tlSumXY - tlSumX * tlSumY) / (tlLen * tlSumX2 - tlSumX * tlSumX);
  const intercept = (tlSumY - slope * tlSumX) / tlLen;

  trendLineData = chartItems.map(item => ({
    x: item.x,
    y: slope * dayjs(item.x).unix() + intercept
  }));

  const data = {
    datasets: [
      {
        type: 'line',
        label: 'COQ',
        borderColor: getColor('primary'),
        borderWidth: 2,
        fill: true,
        data: chartItems,
        tension: 0.0,
        pointStyle: false,
        pointRadius: 0.0
      },
      {
        type: 'line',
        label: 'Trendline',
        borderColor: getColor('warning'),
        borderWidth: 2,
        fill: false,
        data: trendLineData,
        tension: 0.0,
        pointStyle: false,
        pointRadius: 0.0,
        borderDash: [10, 5] // Dashed line: 10px dash, 5px gap
      },
      // {
      //   label: 'Trend Line',
      //   // borderColor: borderColor,
      //   // borderWidth: borderWidth,
      //   // backgroundColor: '#0275d8',
      //   data: chartItems,
      //   trendlineLinear: {
      //     colorMin: 'rgba(255,26,104, 0.5)',
      //     colorMax: 'rgba(255,26,104, 0.5)',
      //     lineStyle: 'dashed',
      //     width: 2,
      //     projection: false,
      //     pointRadius: 0.0,
      //     pointStyle: false,
      //     label: {
      //       color: 'rgba(255,26,104, 1)',
      //       text: 'Trend Line',
      //       display: false,
      //       displayValue: false,
      //       percentage: true
      //     },
      //     // legend: {
      //     //   text: 'Trend Line',
      //     //   borderColor: 'rgba(255,26,104, 1)',
      //     //   strokeStyle: 'rgba(255,26,104, 1)',
      //     //   fillStyle: 'rgba(255,26,104, 1)',
      //     //   lineCap: 'Trend'
      //     // }
      //   }
      // }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        labels: {
          color: getColor('gray-500')
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'day'
        }
      },
      y: {
        ticks: {
          color: getColor('gray-500')
        },
        grid: {
          color: getColor('gray-300'),
          drawBorder: true
        },
        beginAtZero: true
      }
    }
  };

  if (props.isShowingCost && props.isShowingValue) {
    options.scales.y.ticks.beginAtZero = true;
    options.scales.y.ticks.callback = value => {
      return '£' + value;
    };
  } else {
    options.scales.y.ticks.precision = 0;
  }

  // we are showing a percentage
  if(!props.isShowingValue) {
    options.scales.y.ticks.beginAtZero = true;
    options.scales.y.ticks.precision = 3;
    options.scales.y.ticks.callback = value => {
      return value + '%'
    };
  }
  return (
    <Line data={data} options={options} />
  );
}

function COQState(title, value,) {
  return (
    <Card>
      <Card.Header className="pb-0">
        <h6 className="mb-0 mt-2">{title}</h6>
      </Card.Header>
      <Card.Body as={Flex} alignItems="end" justifyContent="between">
        <h4 className={classNames('mb-0 text-700 fw-normal lh-2')}>{value}</h4>
      </Card.Body>
    </Card>
  );
}

export default function COQWidget(props) {
  const widget = props.widget;

  // 
  const [isShowingCost, setIsShowingCost] = useState(WidgetLoadKVBool(widget, 'isShowingCost', true));
  const [isShowingValue, setIsShowingValue] = useState(WidgetLoadKVBool(widget, 'isShowingValue', true));
  
  //  Range slider values
  const [startDate, setStartDate] = useState(dayjs().add(-7, 'day').hour(0));
  const [endDate, setEndDate] = useState(dayjs().add(1, 'day').hour(0));
  // const [dayRange, setDayRange] = useState(7);
  const [rangeSliderValue, setRangeSliderValue] = useState(parseInt(WidgetLoadKV(widget, 'rangeSliderValue', 365 - 7)));

  // Down days map, used to store the down days data to remove from the chart
  const [downDaysMap, setDownDaysMap] = useState({});
  const [itemMap, setItemMap] = useState({});
  const [totalCompanyTurnoverMap, setTotalCompanyTurnoverMap] = useState({});
  const [totalQtyToday, setTotalQtyToday] = useState(0);
  const [totalQtyYesterday, setTotalQtyYesterday] = useState(0);
  const [totalQtyLastWeek, setTotalQtyLastWeek] = useState(0);
  const [totalCostToday, setTotalCostToday] = useState(0);
  const [totalCostYesterday, setTotalCostYesterday] = useState(0);
  const [totalCostLastWeek, setTotalCostLastWeek] = useState(0);
  const [totalCompanyTurnoverToday, setTotalCompanyTurnoverToday] = useState(0);
  const [totalCompanyTurnoverYesterday, setTotalCompanyTurnoverYesterday] = useState(0);
  const [totalCompanyTurnoverLastWeek, setTotalCompanyTurnoverLastWeek] = useState(0);
  const [totalCompanyDeliveredJobCountToday, settotalCompanyDeliveredJobCountToday] = useState(0);
  const [totalCompanyDeliveredJobCountYesterday, settotalCompanyDeliveredJobCountYesterday] = useState(0);
  const [totalCompanyDeliveredJobCountLastWeek, settotalCompanyDeliveredJobCountLastWeek] = useState(0);

  // Calculate the values for today, yesterday and last week
  const calcuateValues = () => {
    let todayQty = 0;
    let yesterdayQty = 0;
    let lastWeekQty = 0;
    let todayCost = 0;
    let yesterdayCost = 0;
    let lastWeekCost = 0;

    let now = dayjs();
    let nowStr = now.format('YYYY-MM-DD');

    let yesterday = dayjs().add(-1, 'day');
    let yesterdayStr = yesterday.format('YYYY-MM-DD');

    let lastWeek = dayjs().add(-7, 'day');

    setItemMap({});
    let coq_map = {};
    for (let i = 0; i < props.data.length; i++) {
      let item = props.data[i];
      let itemDate = dayjs(item.recorded_date);
      let itemDateStr = itemDate.format('YYYY-MM-DD');

      if (!coq_map[itemDateStr]) {
        coq_map[itemDate.format('YYYY-MM-DD')] = {
          items: [],
          totalCost: 0,
          totalCompanyTurnover: 0,
          totalCompanyDeliveredJobCount: 0,
          totalCostPercent: 0,
          downDay: false
        };
      }

      coq_map[itemDate.format('YYYY-MM-DD')].items.push(item);
      coq_map[itemDate.format('YYYY-MM-DD')].totalCost += item.total_cost;
      if (item.down_day) {
        coq_map[itemDate.format('YYYY-MM-DD')].downDay = true;
      }

      let itemCost = item.total_cost;

      // total company turnover
      if (totalCompanyTurnoverMap[itemDateStr]) {
        coq_map[itemDateStr].totalCompanyTurnover = totalCompanyTurnoverMap[itemDateStr].turnover;
        coq_map[itemDateStr].totalCompanyDeliveredJobCount = totalCompanyTurnoverMap[itemDateStr].deliveredJobCount;
        // total cost percentage
        coq_map[itemDateStr].totalCostPercent = (coq_map[itemDateStr].totalCost * 100) / coq_map[itemDateStr].totalCompanyTurnover;

      }

      if (itemDate.isToday()) {
        //, itemDate);
        todayQty += 1;
        todayCost += itemCost;
      }

      if (itemDate.isSame(yesterday, 'day')) {
        //
        yesterdayQty += 1;
        yesterdayCost += itemCost;
      }

      if (itemDate.isBetween(lastWeek, now)) {
        //
        lastWeekQty += 1;
        lastWeekCost += itemCost;
      }

      // 
    }

    // We are showing percentages
    if (isShowingValue) {
      // Get the total company turnover for today, 
      // let totalCompanyTurnoverToday = totalCompanyTurnoverMap[now.format('YYYY-MM-DD')].turnover;
    }

    setItemMap(coq_map);
    setTotalQtyToday(todayQty);
    setTotalQtyYesterday(yesterdayQty);
    setTotalQtyLastWeek(lastWeekQty);
    setTotalCostToday(todayCost.toFixed(2));
    setTotalCostYesterday(yesterdayCost.toFixed(2));
    setTotalCostLastWeek(lastWeekCost.toFixed(2));

    // company turnover
    if (totalCompanyTurnoverMap && nowStr in totalCompanyTurnoverMap) {
      setTotalCompanyTurnoverToday(totalCompanyTurnoverMap[nowStr].turnover);
      settotalCompanyDeliveredJobCountToday(totalCompanyTurnoverMap[nowStr].deliveredJobCount);
    }
    

    if (totalCompanyTurnoverMap && yesterdayStr in totalCompanyTurnoverMap) {
      setTotalCompanyTurnoverYesterday(totalCompanyTurnoverMap[yesterdayStr].turnover);
      settotalCompanyDeliveredJobCountYesterday(totalCompanyTurnoverMap[yesterdayStr].deliveredJobCount);
    }

    // work out last week by summing up the dates
    let current = lastWeek;
    let lastWeekSum = 0
    let itemSum = 0
    while (current.isBefore(now) || current.isSame(now, 'day')) {
      let currentStr = current.format('YYYY-MM-DD')
      if (totalCompanyTurnoverMap && currentStr in totalCompanyTurnoverMap) {
        lastWeekSum += totalCompanyTurnoverMap[currentStr].turnover
        itemSum += totalCompanyTurnoverMap[currentStr].deliveredJobCount
      }

      current = current.add(1, "day"); // Move to next day
    }

    setTotalCompanyTurnoverLastWeek(lastWeekSum);
    settotalCompanyDeliveredJobCountLastWeek(itemSum);

    
  };

  useEffect(() => {
    calcuateValues(isShowingValue);
  }, [downDaysMap, totalCompanyTurnoverMap]);

  useEffect(() => {

    
    getAllDownDays()
      .then(res => {
        let map = {};
        for (var i = 0; i < res.data.length; i++) {
          map[res.data[i].date.substring(0, 10)] = res.data[i];
        }
        setDownDaysMap(map);
      })
      .catch(err => {
        console.log("Failed to get down days", err);
      });

    getTurnover(dayjs().add(-365, 'day'), dayjs()).then(res => {
      let map = {};
      for (let i = 0; i < res.data.length; i++) {
        map[res.data[i].date.substring(0, 10)] = res.data[i];
      }
      setTotalCompanyTurnoverMap(map);
      
    })
      .catch(err => {
        console.log("Failed to get company turnover", err);
      });

    // fetch company turnover
      
      recalculateStartAndEndDates(rangeSliderValue);

  }, []);

  const rangeSliderOnChange = e => {
    setRangeSliderValue(e.target.value);
    // let days = 365 - e.target.value;
    recalculateStartAndEndDates(e.target.value);
    // setDayRange(365 - e.target.value);
    // setStartDate(dayjs().add(-days, 'day').hour(0));
    // setEndDate(dayjs().add(1, 'day').hour(0));
    WidgetSaveKV(widget, 'rangeSliderValue', e.target.value);
  };

  const recalculateStartAndEndDates = (sliderVal) => {
    let days = 365 - sliderVal;
    // setDayRange(365 - e.target.value);
    setStartDate(dayjs().add(-days, 'day').hour(0));
    setEndDate(dayjs().add(1, 'day').hour(0));
  }
  useEffect(() => {
    WidgetSaveKV(widget, 'isShowingValue', isShowingValue);
  }, [isShowingValue]);

  useEffect(() => {
    WidgetSaveKV(widget, 'isShowingCost', isShowingCost);
  }, [isShowingCost]);


  
  return (
    <>
      <div className="">
        <div className=" p-1 text-end">
          <Switch
            className="react-switch"
            checked={isShowingCost}
            onChange={() => {
              setIsShowingCost(!isShowingCost);
            }}
            width={64}
            handleDiameter={20}
            uncheckedIcon={
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  fontSize: 15,
                  color: 'white',
                  paddingRight: 0
                }}
              >
                <b>Qty</b>
              </div>
            }
            checkedIcon={
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  fontSize: 15,
                  color: 'white',
                  paddingRight: 2
                }}
              >
                <b>Cost</b>
              </div>
            }
            onColor={getColor('primary')}
            offColor={getColor('primary')}
            onHandleColor="#d8e2ef"
            offHandleColor="#d8e2ef"
          ></Switch>&nbsp;

          <Switch
            className="react-switch"
            checked={isShowingValue}
            onChange={() => {
              setIsShowingValue(!isShowingValue);
            }}
            width={64}
            handleDiameter={20}
            uncheckedIcon={
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  fontSize: 15,
                  color: 'white',
                  paddingRight: 0
                }}
              >
                <b>&nbsp;&nbsp;%&nbsp;</b>
              </div>
            }
            checkedIcon={
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  fontSize: 15,
                  color: 'white',
                  paddingRight: 2
                }}
              >
                <b>Val</b>
              </div>
            }
            onColor={getColor('primary')}
            offColor={getColor('primary')}
            onHandleColor="#d8e2ef"
            offHandleColor="#d8e2ef"
          ></Switch>

          {/* Cost */}
        </div>
        <Container className="p-2 m-0">

          <Row>
            <Col
              xs={9}
              className="d-none d-md-block"
              style={{ height: '300px', maxHeight: '400px' }}
            >
              <div className="text-center">
                <b>
                  Showing data for {startDate.format('DD/MM/YYYY')}&nbsp;-&nbsp;
                  {endDate.format('DD/MM/YYYY')}{' '}
                </b>
              </div>
              <div style={{ paddingLeft: '10%', paddingRight: '10%' }}>
                <Form.Range
                  className="ml-10"
                  min={1}
                  max={365}
                  step={7}
                  value={rangeSliderValue}
                  onChange={rangeSliderOnChange}
                // onMouseUp={rangeSliderOnMouseUp}
                />
              </div>

              <ChartOptions
                data={props.data}
                startDate={startDate}
                endDate={endDate}
                isShowingCost={isShowingCost}
                itemMap={itemMap}
                downDays={downDaysMap}
                isShowingValue={isShowingValue}
              ></ChartOptions>
            </Col>
            <Col>
              {isShowingValue ?
                <div className="mb-5">
                  {COQState(
                    'Today',
                    isShowingCost ? `£${totalCostToday}` : totalQtyToday
                  )}
                  <br></br>
                  {COQState(
                    'Yesterday',
                    isShowingCost ? `£${totalCostYesterday}` : totalQtyYesterday
                  )}
                  <br></br>
                  {COQState(
                    'Last Week',
                    isShowingCost ? `£${totalCostLastWeek}` : totalQtyLastWeek
                  )}
                </div> :
                <div className="mb-5">
                  {COQState(
                    'Today',
                    isShowingCost ? `${Number((totalCostToday * 100) / totalCompanyTurnoverToday).toFixed(3)}%` : 
                    `${Number((totalQtyToday * 100) / totalCompanyDeliveredJobCountToday).toFixed(3)}%`
                  )}
                  <br></br>
                  {COQState(
                    'Yesterday',
                    isShowingCost ? `${Number((totalCostYesterday * 100) / totalCompanyTurnoverYesterday).toFixed(3)}%` : 
                    `${Number((totalQtyYesterday * 100) / totalCompanyDeliveredJobCountYesterday).toFixed(3)}%`
                  )}
                  <br></br>
                  {COQState(
                    'Last Week',
                    isShowingCost ? `${Number((totalCostLastWeek * 100) / totalCompanyTurnoverLastWeek).toFixed(3)}%` : 
                    `${Number((totalQtyLastWeek * 100) / totalCompanyDeliveredJobCountLastWeek).toFixed(3)}%`
                  )}
                </div>
              }
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
}

COQWidget.propTypes = {
  data: PropTypes.array
};

ChartOptions.propTypes = {
  startDate: PropTypes.object,
  endDate: PropTypes.object
};
